import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Trophy,
  Plus,
  ChevronRight,
  Calendar,
  Clock,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Settings,
  BookOpen,
  Tag,
  Users,
  Download,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

function StatusBadge({ status }) {
  if (status === 'published') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Published
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 gap-1">
      <Clock className="h-3 w-3" />
      Draft
    </Badge>
  );
}

function formatDateRange(start, end) {
  const fmt = (d) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${fmt(start)} — ${fmt(end)}`;
}

function formatCurrency(v) {
  if (v === null || v === undefined || v === 0) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
}

export default function IncentivesPage() {
  const navigate = useNavigate();
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);

  const canManageIncentives = isAdmin || isSeniorScheduler;

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await api.get('/incentives/runs-summary', { params });
      setRuns(res.data);
    } catch {
      // Fallback to basic endpoint if enhanced not available
      try {
        const params = statusFilter !== 'all' ? { status: statusFilter } : {};
        const res = await api.get('/incentives/runs', { params });
        setRuns(res.data);
      } catch (err) {
        const msg = getApiError(err, 'Failed to load incentive runs.');
        setError(msg);
        toast({ title: 'Error', description: msg, variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Summary stats
  const totalRuns = runs.length;
  const publishedRuns = runs.filter(r => r.status === 'published').length;
  const draftRuns = runs.filter(r => r.status === 'draft').length;
  const totalPayout = runs.reduce((s, r) => s + (r.total_payout || 0), 0);

  return (
    <div className="space-y-6" data-testid="incentives-page">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-amber-100">
            <Trophy className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Incentives</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage incentive runs and programme periods</p>
          </div>
        </div>
        {canManageIncentives && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/incentives/types')} className="gap-2" data-testid="incentive-types-btn">
              <Tag className="h-4 w-4" />Types
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/incentives/schemes')} className="gap-2" data-testid="incentive-rules-btn">
              <BookOpen className="h-4 w-4" />Rules
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/incentives/settings')} className="gap-2" data-testid="beneficiary-settings-btn">
              <Settings className="h-4 w-4" />Beneficiary Settings
            </Button>
            <Button onClick={() => navigate('/incentives/new')} className="gap-2" data-testid="create-incentive-run-btn">
              <Plus className="h-4 w-4" />New Incentive Run
            </Button>
          </div>
        )}
      </div>

      {/* Summary dashboard */}
      {!loading && runs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
            <div className="text-2xl font-bold text-slate-900">{totalRuns}</div>
            <div className="text-xs text-slate-500">Total Runs</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
            <div className="text-2xl font-bold text-emerald-700">{publishedRuns}</div>
            <div className="text-xs text-emerald-600">Published</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
            <div className="text-2xl font-bold text-amber-700">{draftRuns}</div>
            <div className="text-xs text-amber-600">Drafts</div>
          </div>
          <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
            <div className="text-2xl font-bold text-indigo-700">{formatCurrency(totalPayout)}</div>
            <div className="text-xs text-indigo-600">Total Payouts</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter-select">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {loading ? '' : `${runs.length} run${runs.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-sm text-slate-600">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchRuns}>Retry</Button>
          </CardContent>
        </Card>
      ) : runs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-20 text-center">
            <div className="flex items-center justify-center h-16 w-16 rounded-full bg-slate-100">
              <Trophy className="h-8 w-8 text-slate-400" />
            </div>
            <div>
              <p className="font-medium text-slate-700">No incentive runs yet</p>
              <p className="text-sm text-slate-500 mt-1">
                {statusFilter !== 'all' ? `No ${statusFilter} runs found.` : 'Create your first incentive run to get started.'}
              </p>
            </div>
            {canManageIncentives && statusFilter === 'all' && (
              <Button onClick={() => navigate('/incentives/new')} className="gap-2" data-testid="empty-create-btn">
                <Plus className="h-4 w-4" />New Incentive Run
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {runs.map((run) => (
            <Card
              key={run.id}
              className="cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all"
              onClick={() => navigate(`/incentives/${run.id}`)}
              data-testid={`incentive-run-row-${run.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="flex items-center justify-center h-8 w-8 rounded-md bg-amber-50 flex-shrink-0 mt-0.5">
                      <Trophy className="h-4 w-4 text-amber-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 text-sm truncate">
                          {run.title || run.incentive_type_name}
                        </span>
                        {run.title && (
                          <span className="text-xs text-slate-500 truncate">{run.incentive_type_name}</span>
                        )}
                        <StatusBadge status={run.status} />
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                        <Calendar className="h-3 w-3 flex-shrink-0" />
                        <span>{formatDateRange(run.period_start, run.period_end)}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        Created by {run.created_by_name || '—'}
                        {run.published_by_name && <> · Published by {run.published_by_name}</>}
                        {run.status === 'published' && run.published_at && (
                          <> · {new Date(run.published_at).toLocaleDateString('en-GB')}</>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Right side: stats */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {run.beneficiary_count > 0 && (
                      <div className="text-right hidden sm:block">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Users className="h-3 w-3" />
                          <span>{run.beneficiary_count} beneficiar{run.beneficiary_count !== 1 ? 'ies' : 'y'}</span>
                        </div>
                        {run.total_payout > 0 && (
                          <div className="text-sm font-semibold text-indigo-700 mt-0.5">{formatCurrency(run.total_payout)}</div>
                        )}
                        {run.completion_pct !== undefined && run.status === 'draft' && (
                          <div className="mt-1">
                            <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${run.completion_pct >= 100 ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{ width: `${Math.min(100, run.completion_pct)}%` }} />
                            </div>
                            <span className="text-[10px] text-slate-400">{run.completion_pct}% complete</span>
                          </div>
                        )}
                      </div>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
